import { logCustomEvent } from './analytics/analytics';
import { 
  sanitizeUrl, 
  computeFingerprint, 
  redactMetadata,
  prepareAnalyticsParams 
} from '../utils/logging';

/**
 * Error severity levels (aligned with industry standards)
 */
export const ErrorSeverity = {
  CRITICAL: 'critical', // App-breaking errors
  ERROR: 'error',       // Failed operations (new)
  WARNING: 'warning',   // Degraded functionality
  INFO: 'info'         // Non-breaking issues
};

/**
 * Error categories for better organization
 */
export const ErrorCategory = {
  MAP: 'map',
  NETWORK: 'network',
  VALIDATION: 'validation',
  PERMISSION: 'permission',
  INITIALIZATION: 'initialization',
  DATA_LOADING: 'data_loading',
  RENDERING: 'rendering',
  UNKNOWN: 'unknown'
};

/**
 * Central error tracking service for Affin
 * Integrates with Firebase Analytics for production monitoring
 */
class ErrorService {
  constructor() {
    this.isDevelopment = process.env.NODE_ENV === 'development';
    this.errorQueue = [];
    this.maxQueueSize = 50;
    this.frequencyMap = new Map(); // Track error frequency by fingerprint
    this.frequencyWindowMs = 60000; // 1 minute window
    this.maxFrequency = 5; // Max errors per fingerprint per window
    
    // Install global error handlers
    this._installGlobalHandlers();
  }

  /**
   * Log an error with context and analytics tracking
   * @param {Error|string} error - The error object or message
   * @param {Object} options - Error tracking options
   * @param {string} options.severity - Error severity level
   * @param {string} options.category - Error category
   * @param {string} options.component - Component where error occurred
   * @param {string} options.action - User action that triggered error
   * @param {Object} options.metadata - Additional error context
   */
  logError(error, options = {}) {
    const {
      severity = ErrorSeverity.WARNING,
      category = ErrorCategory.UNKNOWN,
      component = 'unknown',
      action = 'unknown',
      metadata = {}
    } = options;

    const errorInfo = this._formatError(error, {
      severity,
      category,
      component,
      action,
      metadata
    });

    // Always log to console in development
    if (this.isDevelopment) {
      console.error('[ErrorService]', errorInfo);
    }

    // Check frequency to prevent flooding
    if (this._shouldSendError(errorInfo.fingerprint)) {
      // Track in Firebase Analytics
      this._trackError(errorInfo);
    }

    // Add to error queue for potential batch processing
    this._addToQueue(errorInfo);

    return errorInfo;
  }

  /**
   * Log a warning (convenience method)
   */
  logWarning(message, options = {}) {
    return this.logError(message, {
      ...options,
      severity: ErrorSeverity.WARNING
    });
  }

  /**
   * Log error-level severity (convenience method)
   */
  logErrorLevel(error, options = {}) {
    return this.logError(error, {
      ...options,
      severity: ErrorSeverity.ERROR
    });
  }

  /**
   * Log info-level error (convenience method)
   */
  logInfo(message, options = {}) {
    return this.logError(message, {
      ...options,
      severity: ErrorSeverity.INFO
    });
  }

  /**
   * Log critical error (convenience method)
   */
  logCritical(error, options = {}) {
    return this.logError(error, {
      ...options,
      severity: ErrorSeverity.CRITICAL
    });
  }

  /**
   * Format error for consistent structure with privacy protection
   */
  _formatError(error, context) {
    const isError = error instanceof Error;
    const safeUrl = sanitizeUrl(window.location.href);
    const redactedMetadata = redactMetadata(context.metadata);
    
    const errorInfo = {
      message: isError ? error.message : String(error),
      name: isError ? error.name : 'Error',
      stack: isError && this.isDevelopment ? error.stack : undefined,
      timestamp: new Date().toISOString(),
      ...context,
      metadata: {
        ...redactedMetadata,
        url_path: safeUrl.path,
        url_host: safeUrl.host,
        // Only include user agent in development
        ...(this.isDevelopment ? { userAgent: navigator.userAgent } : {}),
        ...(isError && error.code ? { errorCode: error.code } : {})
      }
    };
    
    // Add fingerprint for deduplication
    errorInfo.fingerprint = computeFingerprint(errorInfo);
    
    return errorInfo;
  }

  /**
   * Track error in Firebase Analytics with proper parameter limits
   */
  _trackError(errorInfo) {
    // Check if analytics is available
    if (typeof logCustomEvent !== 'function') {
      return;
    }
    
    const eventName = `error_${errorInfo.severity}`;
    
    // Use utility to prepare analytics params with all limits enforced
    const eventParams = prepareAnalyticsParams(errorInfo);

    logCustomEvent(eventName, eventParams);
  }

  /**
   * Check if error should be sent based on frequency limits
   */
  _shouldSendError(fingerprint) {
    const now = Date.now();
    const frequencyData = this.frequencyMap.get(fingerprint);
    
    if (!frequencyData) {
      // First occurrence
      this.frequencyMap.set(fingerprint, {
        count: 1,
        windowStart: now
      });
      return true;
    }
    
    // Check if we're in a new time window
    if (now - frequencyData.windowStart > this.frequencyWindowMs) {
      // Reset for new window
      this.frequencyMap.set(fingerprint, {
        count: 1,
        windowStart: now
      });
      return true;
    }
    
    // Check if under frequency limit
    if (frequencyData.count < this.maxFrequency) {
      frequencyData.count++;
      return true;
    }
    
    // Over limit, don't send
    return false;
  }

  /**
   * Add error to queue for potential batch processing
   */
  _addToQueue(errorInfo) {
    this.errorQueue.push(errorInfo);
    
    // Maintain queue size limit
    if (this.errorQueue.length > this.maxQueueSize) {
      this.errorQueue.shift();
    }
  }

  /**
   * Get recent errors (useful for debugging)
   */
  getRecentErrors() {
    return [...this.errorQueue];
  }

  /**
   * Clear error queue
   */
  clearErrorQueue() {
    this.errorQueue = [];
  }

  /**
   * Check if error tracking is available
   */
  isAvailable() {
    return typeof logCustomEvent === 'function';
  }

  /**
   * Install global error handlers for unhandled errors
   */
  _installGlobalHandlers() {
    if (typeof window === 'undefined') return;
    
    // Handle unhandled errors
    window.addEventListener('error', (event) => {
      this.logError(event.error || event.message, {
        severity: ErrorSeverity.ERROR,
        category: ErrorCategory.UNKNOWN,
        component: 'global',
        action: 'window_error',
        metadata: {
          filename: event.filename,
          lineno: event.lineno,
          colno: event.colno
        }
      });
    });
    
    // Handle unhandled promise rejections
    window.addEventListener('unhandledrejection', (event) => {
      this.logError(event.reason || 'Unhandled Promise Rejection', {
        severity: ErrorSeverity.ERROR,
        category: ErrorCategory.UNKNOWN,
        component: 'global',
        action: 'unhandled_rejection',
        metadata: {
          promise: '[Promise]'
        }
      });
    });
  }
}

// Export singleton instance
export const errorService = new ErrorService();

// Export convenience functions
export const logError = (...args) => errorService.logError(...args);
export const logErrorLevel = (...args) => errorService.logErrorLevel(...args);
export const logWarning = (...args) => errorService.logWarning(...args);
export const logInfo = (...args) => errorService.logInfo(...args);
export const logCritical = (...args) => errorService.logCritical(...args);